import * as React from "react";
import { styled, useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import CssBaseline from "@mui/material/CssBaseline";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import Collapse from "@mui/material/Collapse";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import MainHeader from "./Header";
import "./SideMenu.scss";
import logo from "../assets/img/AaysBlack.png";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  FolderOpen,
  UserCheck,
  UserCog,
  LayoutGrid,
  UsersRound,
  CloudCog,
  Clock,
} from "lucide-react";
import { getToken } from "../auth/authConfig";

const drawerWidth = 300;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 10px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 10px)`,
  },
});

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open
    ? {
        ...openedMixin(theme),
        "& .MuiDrawer-paper": {
          ...openedMixin(theme),
          backgroundColor: "#ffffff",
        },
      }
    : {
        ...closedMixin(theme),
        "& .MuiDrawer-paper": {
          ...closedMixin(theme),
          backgroundColor: "#ffffff",
        },
      }),
}));

const Main = styled("main", {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  height: "calc(100vh - 14px)",
  overflow: "hidden",
  overflowY: "auto",
  transition: theme.transitions.create(["margin", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: open
      ? theme.transitions.duration.enteringScreen
      : theme.transitions.duration.leavingScreen,
  }),
}));

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

export default function SideMenu({ children, text }) {
  const theme = useTheme();
  const [open, setOpen] = React.useState(false);
  const [recentInterviews, setRecentInterviews] = React.useState([]);
  const [interviewsOpen, setInterviewsOpen] = React.useState(false);
  const [loadingInterviews, setLoadingInterviews] = React.useState(false);

  const handleDrawerOpen = () => setOpen(true);
  const handleDrawerClose = () => setOpen(false);

  const currentPath = window.location.pathname;
  const [role, setRole] = React.useState("employee");

  const fullMenuList = [
    {
      name: "Overview",
      href: "/",
      icon: LayoutGrid,
      roles: ["HR Admin", "Super Admin", "employee", "Manager"],
    },
    {
      name: "User Management",
      href: "/admin-dashboard",
      icon: UserCheck,
      roles: ["Super Admin", "HR Admin"],
    },
    {
      name: "Employee Portal",
      href: "/employee-dashboard",
      icon: UsersRound,
      roles: ["employee", "Super Admin"],
    },
    {
      name: "Manager Portal",
      href: "/manager-dashboard",
      icon: UserCog,
      roles: ["Manager", "Super Admin"],
    },
    {
      name: "Document Review",
      href: "/document-access/sample",
      icon: FolderOpen,
      roles: ["HR Admin", "Manager", "Super Admin"],
    },
    {
      name: "Interview Sessions",
      href: "/welcome-interview",
      icon: CloudCog,
      roles: ["employee", "Super Admin"],
    },
  ];

  const fetchRole = async () => {
    try {
      const baseURL = `${process.env.REACT_APP_WEB_TOOL_API_BASE_URL}/userRole`;
      const token = await getToken();
      const response = await fetch(baseURL, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "oidc-token": token,
        },
      });

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const data = await response.json();
      console.log("Role data:", data.role);
      setRole(data.role);
      localStorage.setItem("empid", data.EmployeeID);
    } catch (error) {
      console.error("Error fetching role:", error);
    }
  };

  const fetchRecentInterviews = async () => {
    try {
      setLoadingInterviews(true);
      const baseURL = `${process.env.REACT_APP_WEB_TOOL_API_BASE_URL}/get_history`;
      const token = await getToken();
      
      const response = await axios.get(baseURL, {
        headers: {
          "Content-Type": "application/json",
          "oidc-token": token
        },
      });

      if (response.data && Array.isArray(response.data)) {
        setRecentInterviews(response.data);
      }
    } catch (error) {
      console.error("Error fetching recent interviews:", error);
      setRecentInterviews([]);
    } finally {
      setLoadingInterviews(false);
    }
  };

  const handleInterviewsToggle = () => {
    setInterviewsOpen(!interviewsOpen);
    if (!interviewsOpen && recentInterviews.length === 0) {
      fetchRecentInterviews();
    }
  };

  const formatSessionId = (sessionId) => {
    return sessionId ? `Session ${sessionId.substring(0, 8)}...` : 'Unknown Session';
  };

  React.useEffect(() => {
    fetchRole();
  }, []);

  const menuList = React.useMemo(() => {
    return fullMenuList.filter(item => item.roles.includes(role));
  }, [role]);

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />

      <MainHeader open={open} handleDrawerOpen={handleDrawerOpen} text={text} />

      <Drawer className="welcome-screen" variant="permanent" open={open}>
        <DrawerHeader
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: open ? "space-between" : "center",
            px: open ? 2 : 0,
          }}
        >
          <img
            src={logo}
            alt="logo"
            style={{ height: open ? 50 : 40 }}
          />

          {open && (
            <IconButton onClick={handleDrawerClose}>
              {theme.direction === "ltr" ? (
                <ChevronLeftIcon sx={{ color: "#bb1ccc" }} />
              ) : (
                <ChevronRightIcon sx={{ color: "#bb1ccc" }} />
              )}
            </IconButton>
          )}
        </DrawerHeader>

        <Divider />

        <List>
          {menuList.map(({ name, icon: Icon, href }) => {
            const isActive = currentPath === href;
            return (
              <Link style={{ textDecoration: "none" }} to={href} key={name}>
                <ListItem disablePadding sx={{ display: "block" }}>
                  <ListItemButton
                    sx={{
                      minHeight: 58,
                      justifyContent: open ? "initial" : "center",
                      borderRadius: "10px",
                      border: isActive ? "2px solid #bb1ccc" : "none",
                      mb: 0.5,
                      ml: 1,
                      mr: 1,
                      "&:hover": {
                        bgcolor: "rgb(0, 0, 0, .1)",
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: open ? 1.5 : "auto",
                        justifyContent: "center",
                        color: isActive
                          ? "#bb1ccc !important"
                          : "black !important",
                      }}
                    >
                      <Icon />
                    </ListItemIcon>
                    <ListItemText
                      primary={name}
                      sx={{
                        opacity: open ? 1 : 0,
                        color: isActive ? "#bb1ccc" : "black",
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              </Link>
            );
          })}
        </List>

        <Divider />

        {/* Recent Interviews Section */}
        {(role === "employee" || role === "Super Admin") && (
          <>
            <List>
              <ListItem disablePadding sx={{ display: "block" }}>
                <ListItemButton
                  onClick={handleInterviewsToggle}
                  sx={{
                    minHeight: 58,
                    justifyContent: open ? "initial" : "center",
                    borderRadius: "10px",
                    mb: 0.5,
                    ml: 1,
                    mr: 1,
                    "&:hover": {
                      bgcolor: "rgb(0, 0, 0, .1)",
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 1.5 : "auto",
                      justifyContent: "center",
                      color: "black !important",
                    }}
                  >
                    <Clock />
                  </ListItemIcon>
                  <ListItemText
                    primary="Recent Interviews"
                    sx={{
                      opacity: open ? 1 : 0,
                      color: "black",
                    }}
                  />
                  {open && (
                    <IconButton size="small">
                      {interviewsOpen ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  )}
                </ListItemButton>
              </ListItem>
            </List>

            <Collapse in={interviewsOpen && open} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {loadingInterviews ? (
                  <ListItem sx={{ pl: 4 }}>
                    <ListItemText
                      primary="Loading..."
                      sx={{ color: "gray", fontSize: "0.875rem" }}
                    />
                  </ListItem>
                ) : recentInterviews.length > 0 ? (
                  recentInterviews.slice(0, 5).map((interview, index) => (
                    <a
                      key={interview.session_id}
                      href={`/welcome-interview?id=${interview.session_id}`}
                      style={{ textDecoration: "none" }}
                    >
                      <ListItem disablePadding>
                        <ListItemButton
                          sx={{
                            pl: 4,
                            minHeight: 40,
                            borderRadius: "8px",
                            ml: 1,
                            mr: 1,
                            mb: 0.5,
                            "&:hover": {
                              bgcolor: "rgb(187, 28, 204, 0.1)",
                            },
                          }}
                        >
                          <ListItemText
                            primary={interview.project_name || "Untitled Project"}
                            sx={{
                              color: "#666",
                              "& .MuiListItemText-primary": {
                                fontSize: "0.875rem",
                                fontWeight: 500,
                              },
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    </a>
                  ))
                ) : (
                  <ListItem sx={{ pl: 4 }}>
                    <ListItemText
                      primary="No recent interviews"
                      sx={{
                        color: "gray",
                        "& .MuiListItemText-primary": {
                          fontSize: "0.875rem",
                        },
                      }}
                    />
                  </ListItem>
                )}
              </List>
            </Collapse>

            <Divider />
          </>
        )}
      </Drawer>

      <Main
        className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50"
        open={open}
      >
        <DrawerHeader />
        {children}
      </Main>
    </Box>
  );
}